import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { resolveSrc } from "../../../api/endpoints";
import { useMediaQuery } from "react-responsive";

import CardWeatherv2 from "../Widgets/CardWeatherv2";
import { buildLiveEmbedUrl } from "../../../utils/liveEmbed";

function Previewer({ previewContentUrl }) {
  const [resolution, setResolution] = useState("calc((50vh - 100px) * 16 / 9)");
  const isCellPhone = useMediaQuery({ query: "(max-width:480px)" });
  const isWeatherWidget = previewContentUrl?.widget?.widgetCat === "weather";
  const imageUrlRegex = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;
  const isDirectEmbedImage =
    previewContentUrl?.embed === "embed" &&
    imageUrlRegex.test(previewContentUrl?.url || "");
  const embeddedWidgetImageSrc = useMemo(() => {
    if (previewContentUrl?.widget?.widgetCat !== "embed") {
      return null;
    }
    const html = previewContentUrl?.widget?.html?.trim();
    if (!html) {
      return null;
    }

    const imageTagMatch = html.match(/^<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*\/?>(?:\s*)$/i);
    if (imageTagMatch?.[1]) {
      return imageTagMatch[1].replaceAll("&amp;", "&");
    }

    const iframeTagMatch = html.match(/^<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*><\/iframe>(?:\s*)$/i);
    const iframeSrc = iframeTagMatch?.[1]?.replaceAll("&amp;", "&");
    if (iframeSrc && imageUrlRegex.test(iframeSrc)) {
      return iframeSrc;
    }

    return null;
  }, [previewContentUrl]);
  const normalizedEmbedHtml = useMemo(() => {
    if (previewContentUrl?.widget?.widgetCat !== "embed") {
      return "";
    }

    const html = previewContentUrl?.widget?.html || "";
    return html.replace(/<iframe\b[^>]*>/gi, (iframeTag) => {
      let normalizedTag = iframeTag.replace(/\swidth=["'][^"']*["']/gi, "").replace(/\sheight=["'][^"']*["']/gi, "");

      if (/\sscrolling=/i.test(normalizedTag)) {
        normalizedTag = normalizedTag.replace(/\sscrolling=["'][^"']*["']/gi, ' scrolling="no"');
      } else {
        normalizedTag = normalizedTag.replace(/>$/, ' scrolling="no">');
      }

      const forcedFrameStyle = "width:100% !important;height:100% !important;max-width:100% !important;max-height:100% !important;overflow:hidden;";
      if (/\sstyle=["'][^"']*["']/i.test(normalizedTag)) {
        normalizedTag = normalizedTag.replace(/\sstyle=["']([^"']*)["']/i, (_match, styleValue) => {
          return ` style="${styleValue};${forcedFrameStyle}"`;
        });
      } else {
        normalizedTag = normalizedTag.replace(/>$/, ` style="${forcedFrameStyle}">`);
      }

      return normalizedTag;
    });
  }, [previewContentUrl]);
  const constrainedPreviewStyle = {
    height: "100%",
    width: "100%",
    maxWidth: isCellPhone ? "100%" : "960px",
    maxHeight: isCellPhone ? "220px" : "calc(50vh - 100px)",
    margin: "0 auto",
    overflow: "hidden",
  };
  const aspectRatioPreviewStyle = {
    ...constrainedPreviewStyle,
    height: "auto",
    aspectRatio: "16 / 9",
  };
  useEffect(() => {
    isCellPhone && setResolution("calc(200px * 9 / 16)");
    if (previewContentUrl.type == "widget" && previewContentUrl.widget.widgetCat == "scrollingstrip") {
      const htmlCode = document.querySelector("#scrollingstrip");
      htmlCode.innerHTML = previewContentUrl.widget.url;
    }
  }, [isCellPhone, previewContentUrl]);

  return (
    <>
      <Box
        sx={{
          height: isWeatherWidget ? "unset" : "calc(50vh - 100px)",
          width: `100%`,
          margin: isWeatherWidget ? "0" : "20px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .preview-embed-frame iframe, & .preview-embed-frame embed, & .preview-embed-frame object": {
            width: "100% !important",
            height: "100% !important",
            maxWidth: "100% !important",
            maxHeight: "100% !important",
            border: 0,
            display: "block",
          },
        }}>
        {previewContentUrl.type === "image" && <img src={resolveSrc(previewContentUrl.url)} style={{ height: "100%", width: "100%", objectFit: "contain" }} />}
        {previewContentUrl.type === "video" && (
          <video src={resolveSrc(previewContentUrl.url)} style={{ height: "100%", width: "100%", objectFit: "contain" }} controls></video>
        )}
        {previewContentUrl.embed === "embed" && (
          <div style={aspectRatioPreviewStyle}>
            {isDirectEmbedImage ? (
              <img src={previewContentUrl.url} style={{ height: "100%", width: "100%", objectFit: "contain" }} />
            ) : (
              <embed src={`${previewContentUrl.url}`} style={{ height: "100%", width: "100%", objectFit: "contain" }} controls />
            )}
          </div>
        )}
        {previewContentUrl.type === "widget" && (
          <>
            {previewContentUrl?.widget?.widgetCat === "youtube" && (
              <div style={aspectRatioPreviewStyle} id="youtube-widget">
                <iframe src={previewContentUrl.widget.url} style={{ height: "100%", width: "100%" }}></iframe>
              </div>
            )}

            {previewContentUrl?.widget?.widgetCat === "vimeo" && (
              <div style={aspectRatioPreviewStyle}>
                <iframe src={previewContentUrl.widget.url} style={{ height: "100%", width: "100%" }}></iframe>
              </div>
            )}
            {["live_youtube", "live_twitch", "live_vimeo"].includes(previewContentUrl?.widget?.widgetCat) && (
              <div style={aspectRatioPreviewStyle}>
                <iframe
                  src={buildLiveEmbedUrl(previewContentUrl.widget) || ""}
                  allow="autoplay; fullscreen; encrypted-media"
                  allowFullScreen
                  style={{ height: "100%", width: "100%", border: 0 }}
                ></iframe>
              </div>
            )}
            {previewContentUrl.widget.widgetCat === "website" && (
              <div style={aspectRatioPreviewStyle}>
                <iframe src={previewContentUrl.widget.url} style={{ height: "100%", width: "100%" }}></iframe>
              </div>
            )}
            {previewContentUrl.widget.widgetCat === "postermywall" && (() => {
                if (!window.hasOpenedPostermywall) {
                  window.hasOpenedPostermywall = true; 
                  window.open(previewContentUrl.widget.url, "_blank", "noopener,noreferrer");
                }
                return null;
              })()}
            {previewContentUrl.widget.widgetCat === "scrollingstrip" && <div style={constrainedPreviewStyle} id="scrollingstrip"></div>}
            {previewContentUrl.widget.widgetCat === "embed" && (
              embeddedWidgetImageSrc ? (
                <div style={aspectRatioPreviewStyle}>
                  <img src={embeddedWidgetImageSrc} style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                </div>
              ) : (
                <div className="preview-embed-frame" style={aspectRatioPreviewStyle} dangerouslySetInnerHTML={{ __html: normalizedEmbedHtml }}></div>
              )
            )}

            {previewContentUrl.widget.widgetCat === "weather" && (
              <div style={{ height: "calc(80vh - 80px)", width: "90%", position: "relative", overflow: "auto", margin: "0 auto" }}>
                <CardWeatherv2 item={{ ...previewContentUrl.widget }} />
              </div>
            )}

            {previewContentUrl.widget.widgetCat === "canva" && (
                previewContentUrl.widget?.altMediaUrl ? (
                    <img src={previewContentUrl.widget?.altMediaUrl} style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                ) : (
                    <div
                    style={{ ...constrainedPreviewStyle, maxHeight: "100%", overflow: "auto" }}
                    dangerouslySetInnerHTML={{ __html: previewContentUrl.widget.html.replaceAll(">\\n", "<br/>").replaceAll("\\", "").replace(/src=["']https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//gi, 'src="') }}></div>
                )
            )}
          </>
        )}
      </Box>
    </>
  );
}

export default Previewer;
